/**
 * CanvasBlocks — extracted block-type renderers from DashboardContent.js
 * (iter 127 DRY cleanup).
 *
 * Each component receives the full set of helpers it needs as props from
 * the renderFullBlock switch — this keeps every case a pure JSX render
 * without re-binding closures.
 *
 * Common props every block expects:
 *   block         — the block object {id, type, content, style, ...}
 *   content (c)   — block.content shorthand
 *   style (s)     — block.style shorthand
 *   E             — the InlineText-binding helper from DashboardContent
 *   Toolbar       — the hover toolbar JSX element
 *   FullBlock     — the wrapper component (from DashboardParts)
 *   BgLayer       — the background-layer helper (closure in DashboardContent)
 *   onEdit        — (blockId, field, value) => void
 *   onMediaPick   — (blockId, field) => void
 *   imgSizeCls    — image size class derived from s.image_size
 */
import React from 'react';
import axios from 'axios';
import {
  ShoppingBag, MessageSquareQuote, XIcon, Play, CreditCard, Globe,
  Image as ImageIcon,
} from 'lucide-react';
import { ImageUploadZone, MarqueeRenderer, loadFont } from '../content-editor/DashboardParts';
import SectionButtons from '../sqs/SectionButtons';
import FluidCanvas from '../sqs/FluidCanvas';
import WBImage from '../sqs/imageFrameSystem/WBImage';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── PRODUCTS ────────────────────────────────────────────────────────────
export function ProductsBlock({ block, content: c, style: s, E, Toolbar, FullBlock, BgLayer, onEdit, allProducts }) {
  return (
    <FullBlock block={block}>{Toolbar()}
      <BgLayer center hasPadding>
        <E value={c.title} field="title" className="text-3xl font-bold" tag="h2" placeholder="Section Title" />
        <E value={c.subtitle} field="subtitle" className="opacity-60 mt-2 text-lg" tag="p" placeholder="Section description" />
        <div className="flex gap-6 justify-center mt-10 flex-wrap">
          {(c.featured_product_ids || []).length > 0 ? (c.featured_product_ids || []).slice(0, 6).map((pid) => {
            const p = allProducts.find(x => x.id === pid);
            if (!p) return null;
            const img = p.images?.[0] || p.image_url;
            return (
              <div key={pid} className="w-48">
                <div className="w-48 h-56 bg-gray-100 rounded-xl overflow-hidden">
                  {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ShoppingBag size={32} className="text-gray-300" /></div>}
                </div>
                <p className="mt-3 font-medium text-sm">{p.name}</p>
                <p className="opacity-60 text-sm">${p.price?.toFixed(2)}</p>
              </div>
            );
          }) : (
            <div className="w-full py-8 border-2 border-dashed border-current/20 rounded-2xl flex flex-col items-center gap-2 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all" onClick={(e) => { e.stopPropagation(); onEdit(block.id, '_open_panel', true); }}>
              <ShoppingBag size={32} className="opacity-30" /><p className="opacity-40 text-sm font-medium">Click to select products</p>
            </div>
          )}
        </div>
        <SectionButtons block={block} />
      </BgLayer>
    </FullBlock>
  );
}

// ─── ABOUT ───────────────────────────────────────────────────────────────
export function AboutBlock({ block, content: c, style: s, E, Toolbar, FullBlock, BgLayer, onEdit, onMediaPick, imgSizeCls, isImageTop, isImageBottom, isOverlay }) {
  // Iter 172 — Match the section-picker previews exactly. Each preview
  // shape in CategoryShapes.js (FounderLetter / ValuesGrid / Timeline /
  // MissionFocus / Statistics / AboutImageCollage / SplitStory /
  // AlternatingStory) now has a 1:1 canvas counterpart dispatched off
  // `block.layout`. Inline-editable via the same `E` field component used
  // elsewhere so text drag + double-click image swap keep working.
  const layout = block.layout;

  if (layout === 'founder-letter') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <BgLayer defaultBg="bg-[#FAF6EE]" hasPadding>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-[260px_1fr] gap-10 items-center" data-about-layout="founder-letter">
            <div>
              <ImageUploadZone imageUrl={c.image_url} onUpload={(url) => onEdit(block.id, 'image_url', url)} onMediaPick={() => onMediaPick(block.id, 'image_url')} className="rounded-full overflow-hidden w-40 h-40 mx-auto md:mx-0" height="h-40" label="Add Photo" blockId={block.id} field="image_url" frame={(c._image_frames || {}).image_url} />
              <E value={c.eyebrow} field="eyebrow" className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#8B5CF6] mt-6" tag="p" placeholder="A letter from" />
              <E value={c.title} field="title" className="text-3xl font-bold text-[#0E0E23] font-syne mt-1" tag="h2" placeholder="Sarah, our founder" />
            </div>
            <div>
              <E value={c.tagline} field="tagline" className='text-xl italic text-[#0E0E23] leading-relaxed font-["Georgia","serif"] before:content-[""open-quote""] after:content-[""close-quote""]' tag="blockquote" placeholder="Tell your founding story..." />
              {c.signature && <E value={c.signature} field="signature" className="mt-6 text-2xl font-['Caveat','cursive'] text-[#0E0E23]" tag="p" placeholder="Signature" />}
              <SectionButtons block={block} />
            </div>
          </div>
        </BgLayer>
      </FullBlock>
    );
  }

  if (layout === 'values-grid') {
    const items = c.items || [];
    return (
      <FullBlock block={block}>{Toolbar()}
        <BgLayer defaultBg="bg-white" hasPadding>
          <div className="max-w-5xl mx-auto" data-about-layout="values-grid">
            <E value={c.title} field="title" className="text-3xl md:text-4xl font-bold text-[#0E0E23] font-syne text-center mb-10" tag="h2" placeholder="What we stand for" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map((it, i) => (
                <div key={i} className={`p-6 rounded-2xl ${i % 2 ? 'bg-[#F8FAFC]' : 'bg-[#FAF4EB]'}`}>
                  <div className="text-3xl font-bold text-[#0E0E23] font-syne">{String(i + 1).padStart(2, '0')}.</div>
                  <E value={it.title} field={`items.${i}.title`} className="text-lg font-bold text-[#0E0E23] mt-2" tag="h3" placeholder={`Value ${i + 1}`} />
                  {it.text && <E value={it.text} field={`items.${i}.text`} className="text-sm text-[#475569] mt-2 leading-relaxed" tag="p" placeholder="Short description" />}
                </div>
              ))}
            </div>
            <SectionButtons block={block} />
          </div>
        </BgLayer>
      </FullBlock>
    );
  }

  if (layout === 'timeline') {
    const items = c.items || [];
    return (
      <FullBlock block={block}>{Toolbar()}
        <BgLayer defaultBg="bg-white" hasPadding>
          <div className="max-w-3xl mx-auto" data-about-layout="timeline">
            <E value={c.title} field="title" className="text-3xl md:text-4xl font-bold text-[#0E0E23] font-syne mb-10" tag="h2" placeholder="Our journey" />
            <ol className="space-y-6 border-l-2 border-[#E2E8F0] pl-6">
              {items.map((it, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[33px] top-1 w-4 h-4 rounded-full bg-[#8B5CF6] ring-4 ring-white" />
                  <E value={it.year} field={`items.${i}.year`} className="text-lg font-bold text-[#8B5CF6] font-syne" tag="p" placeholder={`Year ${i + 1}`} />
                  <E value={it.text} field={`items.${i}.text`} className="text-sm text-[#0E0E23] mt-1 leading-relaxed" tag="p" placeholder="What happened" />
                </li>
              ))}
            </ol>
            <SectionButtons block={block} />
          </div>
        </BgLayer>
      </FullBlock>
    );
  }

  if (layout === 'mission-focus') {
    // Allow rich-text <em> in title so the highlighted accent words from
    // the preview survive.
    return (
      <FullBlock block={block}>{Toolbar()}
        <div data-about-layout="mission-focus" className="relative bg-[#0E0E23] text-white px-6 sm:px-12 py-20 sm:py-28">
          <div className="max-w-4xl mx-auto">
            <E value={c.eyebrow} field="eyebrow" className="text-[10.5px] tracking-[0.22em] uppercase font-bold opacity-60" tag="p" placeholder="OUR MISSION" />
            <E value={c.title} field="title" className='mt-4 text-4xl sm:text-5xl md:text-6xl leading-[1.05] font-["Georgia","serif"] [&_em]:text-[#F59E0B] [&_em]:not-italic [&_em]:font-bold' tag="h2" placeholder="To make every interaction <em>genuinely useful</em>." />
          </div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  if (layout === 'statistics') {
    const items = c.items || c.stats || [];
    return (
      <FullBlock block={block}>{Toolbar()}
        <BgLayer defaultBg="bg-[#F8FAFC]" hasPadding>
          <div className="max-w-5xl mx-auto text-center" data-about-layout="statistics">
            <E value={c.title} field="title" className="text-3xl md:text-4xl font-bold text-[#0E0E23] font-syne mb-12" tag="h2" placeholder="Impact in numbers" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {items.map((it, i) => (
                <div key={i}>
                  <E value={it.value} field={`items.${i}.value`} className="text-4xl md:text-5xl font-bold text-[#8B5CF6] font-syne" tag="p" placeholder="000" />
                  <E value={it.label} field={`items.${i}.label`} className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-[#475569] mt-3" tag="p" placeholder="Metric" />
                </div>
              ))}
            </div>
            <SectionButtons block={block} />
          </div>
        </BgLayer>
      </FullBlock>
    );
  }

  if (layout === 'image-collage') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <BgLayer defaultBg="bg-white" hasPadding>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-8 items-center" data-about-layout="image-collage">
            <div>
              <E value={c.eyebrow} field="eyebrow" className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#8B5CF6]" tag="p" placeholder="About us" />
              <E value={c.title} field="title" className="text-4xl md:text-5xl font-bold text-[#0E0E23] font-syne mt-2 leading-tight" tag="h2" placeholder="A small studio thinking big." />
              <E value={c.text} field="text" className="text-base text-[#475569] mt-5 leading-relaxed" tag="p" placeholder="Tell your story here..." />
              <SectionButtons block={block} />
            </div>
            <div className="grid grid-rows-2 gap-2 h-[420px]">
              <ImageUploadZone imageUrl={c.image_url} onUpload={(url) => onEdit(block.id, 'image_url', url)} onMediaPick={() => onMediaPick(block.id, 'image_url')} className="rounded-xl overflow-hidden w-full h-full" height="h-full" label="Add Photo" blockId={block.id} field="image_url" frame={(c._image_frames || {}).image_url} />
              <ImageUploadZone imageUrl={c.image_url_2} onUpload={(url) => onEdit(block.id, 'image_url_2', url)} onMediaPick={() => onMediaPick(block.id, 'image_url_2')} className="rounded-xl overflow-hidden w-full h-full" height="h-full" label="Add Photo" blockId={block.id} field="image_url_2" frame={(c._image_frames || {}).image_url_2} />
            </div>
          </div>
        </BgLayer>
      </FullBlock>
    );
  }

  if (layout === 'split-story') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div data-about-layout="split-story" className="bg-white">
          <ImageUploadZone imageUrl={c.image_url} onUpload={(url) => onEdit(block.id, 'image_url', url)} onMediaPick={() => onMediaPick(block.id, 'image_url')} className="w-full h-72 sm:h-96" height="h-full" label="Add Photo" blockId={block.id} field="image_url" frame={(c._image_frames || {}).image_url} />
          <div className="max-w-3xl mx-auto px-6 sm:px-10 py-14 text-center">
            <E value={c.title} field="title" className="text-3xl md:text-4xl font-bold text-[#0E0E23] font-syne leading-tight" tag="h2" placeholder="Built by hand. Backed by craft." />
            <E value={c.text} field="text" className="text-base text-[#475569] mt-5 leading-relaxed" tag="p" placeholder="Tell the story behind your work." />
            <SectionButtons block={block} />
          </div>
        </div>
      </FullBlock>
    );
  }

  if (layout === 'alternating-story') {
    const items = c.items || [];
    return (
      <FullBlock block={block}>{Toolbar()}
        <BgLayer defaultBg="bg-[#FAF6EE]" hasPadding>
          <div className="max-w-5xl mx-auto" data-about-layout="alternating-story">
            <E value={c.title} field="title" className="text-3xl md:text-4xl font-bold text-[#0E0E23] font-syne text-center" tag="h2" placeholder="Our story, chapter by chapter." />
            {c.tagline && <E value={c.tagline} field="tagline" className="text-base text-[#475569] mt-3 text-center max-w-xl mx-auto" tag="p" placeholder="Sub-headline" />}
            <div className="mt-10 space-y-6">
              {items.map((it, i) => (
                <div key={i} className={`grid grid-cols-1 md:grid-cols-2 gap-6 items-center ${i % 2 ? 'md:[&>div:first-child]:order-2' : ''}`}>
                  <ImageUploadZone imageUrl={it.image_url} onUpload={(url) => onEdit(block.id, `items.${i}.image_url`, url)} onMediaPick={() => onMediaPick(block.id, `items.${i}.image_url`)} className="rounded-xl overflow-hidden w-full" height="h-56" label="Add Photo" blockId={block.id} field={`items.${i}.image_url`} frame={(c._image_frames || {})[`items.${i}.image_url`]} />
                  <div>
                    <E value={it.year} field={`items.${i}.year`} className="text-lg font-bold text-[#0E0E23] font-syne" tag="p" placeholder={`Chapter ${i + 1}`} />
                    <E value={it.text} field={`items.${i}.text`} className="text-sm text-[#475569] mt-2 leading-relaxed" tag="p" placeholder="Tell this chapter…" />
                  </div>
                </div>
              ))}
            </div>
            <SectionButtons block={block} />
          </div>
        </BgLayer>
      </FullBlock>
    );
  }

  // Iter 174 — Intro layouts that render via about blocks. Mirror the
  // matching IntroShapes preview shapes (split-image-left, split-image-right,
  // about-split-classic, storytelling-bigtype, offset-asymmetric).
  if (layout === 'split-image-left' || layout === 'about-split-classic') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div data-about-layout={layout} className="relative bg-white grid grid-cols-1 md:grid-cols-2 min-h-[440px]">
          <div className="relative min-h-[260px]">
            <ImageUploadZone imageUrl={c.image_url} onUpload={(url) => onEdit(block.id, 'image_url', url)} onMediaPick={() => onMediaPick(block.id, 'image_url')} className="w-full h-full" height="h-full" label="Add Photo" blockId={block.id} field="image_url" frame={(c._image_frames || {}).image_url} />
          </div>
          <div className="p-10 sm:p-14 flex flex-col justify-center">
            {c.eyebrow && <E value={c.eyebrow} field="eyebrow" className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#8B5CF6]" tag="p" placeholder="Issue 01" />}
            <E value={c.title} field="title" className="text-4xl font-bold text-[#0E0E23] leading-tight mt-2 font-syne" tag="h2" placeholder={layout === 'about-split-classic' ? 'About Us' : 'Stories worth telling slowly.'} />
            {(c.text || c.tagline) && <E value={c.text || c.tagline} field={c.text ? 'text' : 'tagline'} className="text-base text-gray-600 mt-4 leading-relaxed" tag="p" placeholder="Tell your story…" />}
            <SectionButtons block={block} />
          </div>
        </div>
      </FullBlock>
    );
  }

  if (layout === 'split-image-right') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div data-about-layout="split-image-right" className="relative bg-[#FFFBF5] grid grid-cols-1 md:grid-cols-2 min-h-[440px]">
          <div className="p-10 sm:p-14 flex flex-col justify-center">
            <E value={c.title} field="title" className="text-5xl font-bold text-[#0E0E23] leading-[1.05] font-syne" tag="h2" placeholder="Hand-built for makers." />
            <E value={c.tagline} field="tagline" className="text-base text-gray-600 mt-4 leading-relaxed" tag="p" placeholder="Tools that respect the craft." />
            <SectionButtons block={block} />
          </div>
          <div className="relative min-h-[260px] md:order-last">
            <ImageUploadZone imageUrl={c.image_url} onUpload={(url) => onEdit(block.id, 'image_url', url)} onMediaPick={() => onMediaPick(block.id, 'image_url')} className="w-full h-full" height="h-full" label="Add Photo" blockId={block.id} field="image_url" frame={(c._image_frames || {}).image_url} />
          </div>
        </div>
      </FullBlock>
    );
  }

  if (layout === 'storytelling-bigtype') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div data-about-layout="storytelling-bigtype" className="relative bg-white px-6 sm:px-12 py-24">
          <div className="max-w-4xl mx-auto">
            <E value={c.eyebrow} field="eyebrow" className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#8B5CF6]" tag="p" placeholder="A story" />
            <E value={c.title || c.tagline} field={c.title ? 'title' : 'tagline'} className='mt-4 text-5xl sm:text-6xl font-bold text-[#0E0E23] leading-tight font-["Georgia","serif"]' tag="h2" placeholder="We built this for the people who notice the stitching." />
            <SectionButtons block={block} />
          </div>
        </div>
      </FullBlock>
    );
  }

  // ── Iter 189c Phase B — NEW premium About variants ──
  // FOUNDER-STORY-PHOTO — big-photo split (55% left photo, 45% right story).
  // Uses portrait aspect for the photo, an eyebrow, a serif tagline pull-
  // quote, and body copy. Great for solo-founder brands who want a strong
  // human moment as their About section.
  if (layout === 'founder-story-photo') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div data-about-layout="founder-story-photo" className="relative bg-white grid grid-cols-1 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] min-h-[560px]">
          <div className="relative min-h-[380px] md:min-h-full">
            <ImageUploadZone imageUrl={c.image_url} onUpload={(url) => onEdit(block.id, 'image_url', url)} onMediaPick={() => onMediaPick(block.id, 'image_url')} className="w-full h-full absolute inset-0" height="h-full" label="Add Photo" blockId={block.id} field="image_url" frame={(c._image_frames || {}).image_url} />
          </div>
          <div className="p-10 sm:p-16 flex flex-col justify-center bg-[#FAF6EE]">
            <E value={c.eyebrow} field="eyebrow" className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#8B5CF6]" tag="p" placeholder="MEET" />
            <E value={c.title} field="title" className='mt-3 text-5xl sm:text-6xl font-bold text-[#0E0E23] leading-[1.02] font-["Playfair_Display","Georgia",serif]' tag="h2" placeholder="Maya Cortez" />
            <E value={c.tagline} field="tagline" className="mt-5 text-lg italic text-[#0E0E23]/75 leading-relaxed font-['Georgia','serif']" tag="p" placeholder='"Design that respects the past and reaches for the future."' />
            <E value={c.text} field="text" className="mt-5 text-base text-[#475569] leading-relaxed" tag="p" placeholder="Six years ago I set out to build products that felt hand-crafted. Today we still do — every stitch, every line, every pixel." />
            <SectionButtons block={block} />
          </div>
        </div>
      </FullBlock>
    );
  }

  // COLLAGE-VALUES — 3-image editorial collage on top + 3-column values row
  // below in one section. Combines the visual richness of image-collage
  // with the trust-signal of values-grid. Ideal for craft/luxury brands.
  if (layout === 'collage-values') {
    const items = c.items || [];
    return (
      <FullBlock block={block}>{Toolbar()}
        <div data-about-layout="collage-values" className="relative bg-white px-6 sm:px-12 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-3 gap-3 mb-14">
              {[0, 1, 2].map((i) => (
                <div key={i} className={`relative aspect-[3/4] overflow-hidden rounded-lg ${i === 1 ? 'translate-y-6' : ''}`}>
                  <ImageUploadZone imageUrl={c[`image_url_${i + 1}`] || c.images?.[i]} onUpload={(url) => onEdit(block.id, `image_url_${i + 1}`, url)} onMediaPick={() => onMediaPick(block.id, `image_url_${i + 1}`)} className="w-full h-full absolute inset-0" height="h-full" label="Add Photo" blockId={block.id} field={`image_url_${i + 1}`} frame={(c._image_frames || {})[`image_url_${i + 1}`]} />
                </div>
              ))}
            </div>
            <div className="text-center">
              <E value={c.eyebrow} field="eyebrow" className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#8B5CF6]" tag="p" placeholder="WHY IT MATTERS" />
              <E value={c.title} field="title" className="mt-3 text-4xl md:text-5xl font-bold text-[#0E0E23] font-syne" tag="h2" placeholder="Three things we care about." />
            </div>
            {items.length > 0 && (
              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                {items.slice(0, 3).map((it, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl font-bold text-[#8B5CF6] font-syne">{String(i + 1).padStart(2, '0')}.</div>
                    <E value={it.title} field={`items.${i}.title`} className="mt-3 text-lg font-bold text-[#0E0E23]" tag="h3" placeholder={`Value ${i + 1}`} />
                    <E value={it.text} field={`items.${i}.text`} className="mt-2 text-sm text-[#475569] leading-relaxed" tag="p" placeholder="Short description." />
                  </div>
                ))}
              </div>
            )}
            <SectionButtons block={block} />
          </div>
        </div>
      </FullBlock>
    );
  }

  // Default (legacy) layout — image + text side-by-side / overlay / top.
  return (
    <FullBlock block={block}>{Toolbar()}
      <BgLayer defaultBg="bg-gray-50" hasPadding>
        {isImageTop || isImageBottom ? (
          <div className={`max-w-5xl mx-auto flex flex-col items-center gap-8 ${isImageBottom ? 'flex-col-reverse' : ''}`}>
            <div className={`w-full md:w-2/3 ${imgSizeCls}`}>
              <ImageUploadZone imageUrl={c.image_url} onUpload={(url) => onEdit(block.id, 'image_url', url)} onMediaPick={() => onMediaPick(block.id, 'image_url')} className="rounded-2xl overflow-hidden w-full" height="h-72" label="Add Photo" blockId={block.id} field="image_url" frame={(c._image_frames || {}).image_url} />
            </div>
            <div className="text-center max-w-2xl">
              <E value={c.title} field="title" className="text-3xl font-bold" tag="h2" placeholder="About Us" />
              <E value={c.text} field="text" className="mt-4 leading-relaxed text-lg opacity-80" tag="p" placeholder="Tell your story here..." />
              {c.vision && <E value={c.vision} field="vision" className="mt-4 italic opacity-70" tag="p" placeholder="Your vision" />}
              <SectionButtons block={block} />
            </div>
          </div>
        ) : isOverlay ? (
          <div className="relative max-w-5xl mx-auto h-[420px] rounded-3xl overflow-hidden">
            {c.image_url && (
              <WBImage
                src={c.image_url}
                blockId={block.id}
                field="image_url"
                frame={(c._image_frames || {}).image_url}
                className="absolute inset-0 w-full h-full"
              />
            )}
            <div className="absolute inset-0 bg-black/50 pointer-events-none" />
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-8 text-white">
              <E value={c.title} field="title" className="text-4xl font-bold" tag="h2" placeholder="About Us" />
              <E value={c.text} field="text" className="mt-4 leading-relaxed text-lg max-w-2xl" tag="p" placeholder="Tell your story here..." />
              <SectionButtons block={block} />
            </div>
          </div>
        ) : (
          <div className={`max-w-5xl mx-auto flex gap-10 items-center flex-wrap md:flex-nowrap ${s.alignment === 'right' ? 'md:flex-row-reverse' : ''}`}>
            <div className={`w-full md:w-2/5 shrink-0 mx-auto ${imgSizeCls}`}>
              <ImageUploadZone imageUrl={c.image_url} onUpload={(url) => onEdit(block.id, 'image_url', url)} onMediaPick={() => onMediaPick(block.id, 'image_url')} className="rounded-2xl overflow-hidden w-full" height="h-72" label="Add Photo" blockId={block.id} field="image_url" frame={(c._image_frames || {}).image_url} />
            </div>
            <div className="flex-1">
              <E value={c.title} field="title" className="text-3xl font-bold" tag="h2" placeholder="About Us" />
              <E value={c.text} field="text" className="mt-4 leading-relaxed text-lg opacity-80" tag="p" placeholder="Tell your story here..." />
              {c.vision && <E value={c.vision} field="vision" className="mt-4 italic opacity-70" tag="p" placeholder="Your vision" />}
              <SectionButtons block={block} />
            </div>
          </div>
        )}
      </BgLayer>
    </FullBlock>
  );
}

// ─── GALLERY ─────────────────────────────────────────────────────────────
export function GalleryBlock({ block, content: c, style: s, E, Toolbar, FullBlock, BgLayer, onEdit, onMediaPick }) {
  return (
    <FullBlock block={block}>{Toolbar()}
      <BgLayer hasPadding>
        <E value={c.title} field="title" className="text-3xl font-bold text-center mb-8" tag="h2" placeholder="Gallery" />
        <div className={`max-w-5xl mx-auto ${c.layout === 'lookbook' ? 'space-y-4' : 'grid grid-cols-2 md:grid-cols-3 gap-4'}`}>
          {(c.images || []).map((img, i) => {
            const url = typeof img === 'string' ? img : img.url;
            const imgFrame = ((c._image_frames || {})[`images.${i}`]) || (c._image_frames || {}).images;
            return (
              <div key={i} className={`overflow-hidden rounded-xl group/img relative ${c.layout === 'lookbook' ? 'h-64' : 'aspect-square'}`}>
                <WBImage src={url} alt="" blockId={block.id} field={`images.${i}`} frame={imgFrame} className="w-full h-full" />
                <button onClick={(e) => { e.stopPropagation(); const newImgs = [...(c.images || [])]; newImgs.splice(i, 1); onEdit(block.id, 'images', newImgs); }} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover/img:opacity-100 transition-all z-10"><XIcon size={12} /></button>
              </div>
            );
          })}
          <div className={`border-2 border-dashed border-current/20 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all ${c.layout === 'lookbook' ? 'h-32' : 'aspect-square'}`}>
            <ImageUploadZone imageUrl="" onUpload={(url) => onEdit(block.id, 'images', [...(c.images || []), url])} onMediaPick={() => onMediaPick(block.id, 'images')} height="h-full" className="w-full h-full !border-0 !bg-transparent" label="Add Image" />
          </div>
        </div>
        <SectionButtons block={block} />
      </BgLayer>
    </FullBlock>
  );
}

// ─── TESTIMONIAL ─────────────────────────────────────────────────────────
// ─── TESTIMONIAL ─────────────────────────────────────────────────────────
// Iter 178g — Testimonials Section Audit. Four layout variants so each
// `testimonial` template renders the visual its preview tile promises.
// (Layouts: single · three-up · with-image · with-cta)
export function TestimonialBlock({ block, content: c, style: s, E, Toolbar, FullBlock, BgLayer }) {
  const layout = block.layout || 'single';

  if (layout === 'three-up') {
    const items = c.items || [
      { quote: 'Loved it.', author: 'A.' },
      { quote: 'Would recommend.', author: 'B.' },
      { quote: 'Five stars.', author: 'C.' },
    ];
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-16 px-8 bg-white">
          {c.title && <E value={c.title} field="title" className="text-3xl font-extrabold text-gray-900 text-center mb-10" tag="h2" placeholder="What clients say" />}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {items.slice(0,3).map((it,i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-6">
                <div className="text-amber-500 text-lg">★★★★★</div>
                <p className="mt-3 text-gray-900 italic leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>&ldquo;{it.quote}&rdquo;</p>
                <p className="mt-4 text-xs font-extrabold tracking-wider uppercase text-gray-500">— {it.author}</p>
              </div>
            ))}
          </div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  if (layout === 'with-image') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="grid grid-cols-1 md:grid-cols-2 bg-white">
          <div className="aspect-square md:aspect-auto relative">
            {c.image
              ? <img src={c.image} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-gradient-to-br from-amber-100 to-rose-200" />}
          </div>
          <div className="p-10 md:p-16 flex flex-col justify-center">
            <div className="text-amber-500 text-2xl">★★★★★</div>
            <p className="mt-4 text-2xl md:text-3xl text-gray-900 italic leading-snug" style={{ fontFamily: 'Georgia, serif' }}>&ldquo;<E value={c.quote} field="quote" tag="span" placeholder="Outstanding craftsmanship." />&rdquo;</p>
            <p className="mt-6 text-sm font-extrabold tracking-wider uppercase text-gray-500">— <E value={c.author} field="author" tag="span" placeholder="Customer Name" /> · <E value={c.role} field="role" tag="span" placeholder="Returning" /></p>
          </div>
        </div>
      </FullBlock>
    );
  }

  if (layout === 'with-cta') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-16 px-8 bg-white">
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <p className="text-2xl text-gray-900 italic" style={{ fontFamily: 'Georgia, serif' }}>&ldquo;<E value={c.quote} field="quote" tag="span" placeholder="Game changer." />&rdquo;</p>
              <p className="mt-4 text-xs font-extrabold tracking-wider uppercase text-gray-500">— <E value={c.author} field="author" tag="span" placeholder="Customer" /></p>
            </div>
            <div className="mt-6 text-center">
              <button className="px-8 py-3 rounded-full text-xs font-extrabold uppercase tracking-wider text-white" style={{ background: '#8B5CF6' }}>{c.cta_label || 'Read more reviews →'}</button>
            </div>
          </div>
        </div>
      </FullBlock>
    );
  }

  // Default · 'single' — cream bg, centered italic quote with big violet open-quote
  return (
    <FullBlock block={block}>{Toolbar()}
      <div className="py-20 px-8" style={{ background: '#FAF6EE' }}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-6xl leading-none mb-2" style={{ color: '#8B5CF6', fontFamily: 'Georgia, serif' }}>&ldquo;</div>
          <E value={c.quote} field="quote" className="text-2xl md:text-3xl italic text-gray-900 leading-snug" tag="p" placeholder="A truly exceptional team." />
          <p className="mt-6 text-xs font-extrabold tracking-[0.2em] uppercase text-gray-500">— <E value={c.author} field="author" tag="span" placeholder="Customer Name" /></p>
        </div>
        <SectionButtons block={block} />
      </div>
    </FullBlock>
  );
}

// ─── SIGNUP ──────────────────────────────────────────────────────────────
// Iter 178g — Newsletter audit. Five layout variants so each newsletter
// template renders the visual its preview tile promises.
export function SignupBlock({ block, content: c, style: s, E, Toolbar, FullBlock, BgLayer }) {
  const layout = block.layout || 'banner';

  if (layout === 'inline') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-16 px-8" style={{ background: '#FAF6EE' }}>
          <div className="max-w-md mx-auto text-center">
            <E value={c.title} field="title" className="text-2xl font-extrabold text-gray-900" tag="h2" placeholder="Stay in the loop" />
            <E value={c.subtitle} field="subtitle" className="text-sm text-gray-500 mt-1" tag="p" placeholder="Get updates monthly" />
            <div className="flex gap-2 mt-5">
              <div className="flex-1 h-11 bg-white border border-gray-200 rounded-lg px-3 flex items-center text-sm text-gray-400">you@email.com</div>
              <button className="px-5 h-11 bg-gray-900 text-white rounded-lg text-xs font-extrabold tracking-wider uppercase">Join</button>
            </div>
          </div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  if (layout === 'incentive') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-16 px-8 bg-white">
          <div className="max-w-md mx-auto text-center">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider" style={{ background: '#10B98115', color: '#10B981' }}>▼ 10% OFF</span>
            <E value={c.title} field="title" className="text-3xl font-extrabold text-gray-900 mt-3" tag="h2" placeholder="Get 10% off your first order" />
            <E value={c.subtitle} field="subtitle" className="text-sm text-gray-500 mt-1" tag="p" placeholder="Just enter your email" />
            <div className="flex gap-2 mt-5">
              <div className="flex-1 h-11 bg-gray-50 border border-gray-200 rounded-lg px-3 flex items-center text-sm text-gray-400">email</div>
              <button className="px-5 h-11 rounded-lg text-xs font-extrabold tracking-wider uppercase text-white" style={{ background: '#10B981' }}>Claim</button>
            </div>
          </div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  if (layout === 'footer') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-10 px-8 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <E value={c.title} field="title" className="text-xl font-extrabold" tag="h3" placeholder="Newsletter" />
              <E value={c.subtitle} field="subtitle" className="text-xs opacity-60 mt-1" tag="p" placeholder="Weekly digest in your inbox" />
            </div>
            <div className="flex gap-2 flex-1 w-full">
              <div className="flex-1 h-11 bg-white/10 border border-white/15 rounded-lg px-3 flex items-center text-sm text-white/40">email</div>
              <button className="px-5 h-11 rounded-lg text-xs font-extrabold tracking-wider uppercase text-white" style={{ background: '#F97316' }}>OK</button>
            </div>
          </div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  if (layout === 'launch') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-20 px-8" style={{ background: '#FAF6EE' }}>
          <div className="max-w-md mx-auto text-center">
            <p className="text-xs font-extrabold tracking-[0.25em] uppercase" style={{ color: '#8B5CF6' }}>Coming Q2 2026</p>
            <E value={c.title} field="title" className="text-3xl font-extrabold text-gray-900 mt-3" tag="h2" placeholder="Get notified at launch" />
            <E value={c.subtitle} field="subtitle" className="text-sm text-gray-500 mt-2" tag="p" placeholder="" />
            <div className="flex gap-2 mt-5">
              <div className="flex-1 h-11 bg-white border border-gray-200 rounded-lg px-3 flex items-center text-sm text-gray-400">you@email.com</div>
              <button className="px-5 h-11 rounded-lg text-xs font-extrabold tracking-wider uppercase text-white" style={{ background: '#8B5CF6' }}>Notify Me</button>
            </div>
          </div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  if (layout === 'magazine-inline') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-20 px-6" style={{ background: '#FAF6EE' }}>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <p className="text-xs font-extrabold uppercase tracking-[0.24em]" style={{ color: '#8B5CF6' }}>ISSUE 12 · FREE MONTHLY</p>
              <E value={c.title} field="title" className="mt-3 text-4xl md:text-5xl font-extrabold leading-tight text-gray-900" tag="h2" placeholder="Stories for people who notice the stitching." />
              <E value={c.subtitle} field="subtitle" className="mt-3 text-base text-gray-600 leading-relaxed" tag="p" placeholder="A free monthly dispatch — long-form essays, studio visits, and quiet obsessions. One email, first Sunday of the month." />
              <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg">
                <div className="flex-1 h-12 bg-white border border-gray-200 rounded-lg px-4 flex items-center text-sm text-gray-400">you@email.com</div>
                <button className="h-12 px-6 rounded-lg text-xs font-extrabold uppercase tracking-wider text-white" style={{ background: '#0E0E23' }}>Subscribe Free</button>
              </div>
              <p className="mt-3 text-xs text-gray-400">No spam. Unsubscribe with one click.</p>
            </div>
            <div className="md:col-span-5">
              <div className="aspect-[3/4] w-full rounded-lg overflow-hidden bg-gradient-to-br from-amber-100 via-pink-100 to-violet-100 relative shadow-2xl">
                {c.cover_image
                  ? <img src={c.cover_image} alt="" className="w-full h-full object-cover" />
                  : <img src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=80" alt="" className="w-full h-full object-cover" />
                }
                <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/70 to-transparent text-white">
                  <p className="text-[10px] font-extrabold uppercase tracking-wider opacity-70">This Issue</p>
                  <p className="mt-1 text-lg font-extrabold leading-tight">The new quiet luxury</p>
                </div>
              </div>
            </div>
          </div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  // Default · 'banner' — bold full-bleed violet→pink gradient (matches NBanner preview)
  return (
    <FullBlock block={block}>{Toolbar()}
      <div className="py-20 px-8 text-center" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)' }}>
        <div className="max-w-xl mx-auto text-white">
          <E value={c.title} field="title" className="text-5xl font-extrabold" tag="h2" placeholder="Subscribe" />
          <E value={c.subtitle} field="subtitle" className="opacity-90 mt-3 text-base" tag="p" placeholder="Never miss a drop" />
          <div className="flex gap-2 justify-center mt-8 max-w-md mx-auto">
            <div className="flex-1 h-12 bg-white/20 border border-white/30 rounded-lg px-4 flex items-center text-sm text-white/70">you@email.com</div>
            <button className="px-6 h-12 bg-white text-gray-900 rounded-lg text-xs font-extrabold uppercase tracking-wider">Subscribe</button>
          </div>
          <SectionButtons block={block} />
        </div>
      </div>
    </FullBlock>
  );
}

// ─── CUSTOM TEXT ─────────────────────────────────────────────────────────
const titleSizeMap = { sm: 'text-xl', md: 'text-2xl', lg: 'text-3xl', xl: 'text-4xl', xxl: 'text-5xl md:text-6xl' };
const bodySizeMap  = { sm: 'text-sm', md: 'text-base', lg: 'text-lg', xl: 'text-xl', xxl: 'text-2xl' };
export function CustomTextBlock({ block, content: c, style: s, E, Toolbar, FullBlock, BgLayer }) {
  const sz = c.font_size || 'lg';
  const ff = c.font_family ? loadFont(c.font_family) && c.font_family : undefined;
  return (
    <FullBlock block={block}>{Toolbar()}
      <BgLayer center hasPadding>
        <div className="max-w-3xl mx-auto" style={{ textAlign: c.alignment || 'center', fontFamily: ff || undefined }}>
          {c.title && <E value={c.title} field="title" className={`${titleSizeMap[sz] || titleSizeMap.lg} font-bold`} tag="h2" placeholder="Heading" />}
          <E value={c.text} field="text" className={`mt-3 leading-relaxed ${bodySizeMap[sz] || bodySizeMap.lg}`} tag="p" placeholder="Your text content here..." />
          <SectionButtons block={block} />
        </div>
      </BgLayer>
    </FullBlock>
  );
}

// ─── SINGLE IMAGE ────────────────────────────────────────────────────────
export function SingleImageBlock({ block, content: c, style: s, E, Toolbar, FullBlock, BgLayer, onEdit, onMediaPick, imgSizeCls }) {
  const imgFilter = c.style?.filter && c.style.filter !== 'none' ? c.style.filter : '';
  const isFullBleed = c.shape === 'fullbleed';
  const imgRadius = c.shape === 'circle' ? '50%' : (typeof c.style?.radius === 'number' ? `${c.style.radius}px` : undefined);
  const shapeCls = c.shape === 'circle'
    ? '!rounded-full aspect-square'
    : (c.shape === 'rounded' ? 'rounded-2xl'
       : (isFullBleed ? 'rounded-none' : 'rounded-md'));

  // Iter 178i — `fullbleed` shape: edge-to-edge, no padding, no max-width.
  if (isFullBleed) {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className={`w-full overflow-hidden ${shapeCls}`} style={imgFilter ? { filter: imgFilter } : {}} data-img-style-wrap>
          <ImageUploadZone imageUrl={c.image_url} onUpload={(url) => onEdit(block.id, 'image_url', url)} onMediaPick={() => onMediaPick(block.id, 'image_url')} className="w-full" height="h-[60vh]" label="Add Image" blockId={block.id} field="image_url" frame={(c._image_frames || {}).image_url} />
        </div>
        {c.caption && <p className="opacity-60 text-sm text-center mt-3"><E value={c.caption} field="caption" tag="span" placeholder="Caption" /></p>}
      </FullBlock>
    );
  }

  return (
    <FullBlock block={block}>{Toolbar()}
      <BgLayer hasPadding>
        <div style={{ textAlign: c.alignment || 'center' }}>
          <div
            className={`mx-auto overflow-hidden ${imgSizeCls} ${shapeCls}`}
            style={{
              ...(imgRadius ? { borderRadius: imgRadius } : {}),
              ...(imgFilter ? { filter: imgFilter } : {}),
            }}
            data-img-style-wrap
          >
            <ImageUploadZone imageUrl={c.image_url} onUpload={(url) => onEdit(block.id, 'image_url', url)} onMediaPick={() => onMediaPick(block.id, 'image_url')} className="w-full h-full" height="h-64" label="Add Image" blockId={block.id} field="image_url" frame={(c._image_frames || {}).image_url} />
          </div>
          {c.caption && <E value={c.caption} field="caption" className="opacity-60 text-sm mt-3" tag="p" placeholder="Caption" />}
          <SectionButtons block={block} />
        </div>
      </BgLayer>
    </FullBlock>
  );
}

// ─── VIDEO ───────────────────────────────────────────────────────────────
// Iter 178g — Videos Section Audit. Five layout variants so each video
// template renders the visual its preview tile promises.
export function VideoBlock({ block, content: c, E, Toolbar, FullBlock, onEdit }) {
  const layout = block.layout || 'hero';

  if (layout === 'grid') {
    const slots = c.videos && c.videos.length ? c.videos : [{}, {}, {}, {}];
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-16 px-8 bg-white">
          {c.title && <E value={c.title} field="title" className="text-3xl font-extrabold text-gray-900 text-center mb-10" tag="h2" placeholder="Featured Videos" />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {slots.slice(0,4).map((v, i) => (
              <div key={i} className="aspect-video rounded-xl overflow-hidden bg-gray-900 relative cursor-pointer group" onClick={(e) => { e.stopPropagation(); onEdit(block.id, '_open_panel', true); }}>
                {v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-all">
                    <Play size={20} className="text-gray-900 ml-1" />
                  </div>
                </div>
                {v.title && <div className="absolute bottom-3 left-3 text-white text-sm font-bold">{v.title}</div>}
              </div>
            ))}
          </div>
        </div>
      </FullBlock>
    );
  }

  if (layout === 'testimonial') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-16 px-8 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-10 items-center">
            <div className="relative aspect-square w-40 md:w-48 rounded-full overflow-hidden flex-shrink-0 cursor-pointer group" onClick={(e) => { e.stopPropagation(); onEdit(block.id, '_open_panel', true); }}>
              {c.thumbnail
                ? <img src={c.thumbnail} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-gradient-to-br from-violet-600 to-pink-500" />}
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center group-hover:scale-110 transition-all">
                  <Play size={22} className="text-gray-900 ml-1" />
                </div>
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs font-extrabold tracking-[0.25em] uppercase opacity-60">Customer Story</p>
              <E value={c.title} field="title" className="text-2xl md:text-3xl font-extrabold mt-3" tag="h2" placeholder="Hear from our customers" />
              <E value={c.quote} field="quote" className="text-base opacity-80 mt-4 leading-relaxed" tag="p" placeholder="Their words in their own voice — paste a short pull-quote here." />
              <p className="text-xs opacity-60 mt-3">— {c.author || 'Customer Name'}</p>
            </div>
          </div>
        </div>
      </FullBlock>
    );
  }

  if (layout === 'banner') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="relative h-[480px] overflow-hidden cursor-pointer group" onClick={(e) => { e.stopPropagation(); onEdit(block.id, '_open_panel', true); }}>
          {c.thumbnail
            ? <img src={c.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover" />
            : <div className="absolute inset-0 bg-gradient-to-br from-amber-200 to-orange-500" />}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70" />
          <div className="absolute bottom-10 left-10 text-white max-w-xl">
            <span className="inline-block px-3 py-1 rounded-full bg-white text-gray-900 text-xs font-extrabold uppercase tracking-wider">▶ Featured</span>
            <E value={c.title} field="title" className="text-4xl md:text-5xl font-extrabold mt-4 leading-tight" tag="h2" placeholder="Cinematic banner" />
            <E value={c.subtitle} field="subtitle" className="opacity-90 mt-3 text-base" tag="p" placeholder="Tap to play" />
          </div>
          <div className="absolute right-10 bottom-10">
            <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center group-hover:scale-110 transition-all shadow-2xl">
              <Play size={26} className="text-gray-900 ml-1" />
            </div>
          </div>
        </div>
      </FullBlock>
    );
  }

  // Default · 'hero' — single large video, centered, with optional title above
  return (
    <FullBlock block={block}>{Toolbar()}
      <div className="py-12 px-8 bg-white text-center">
        {c.title && <E value={c.title} field="title" className="text-2xl font-bold text-gray-900 mb-4" tag="h2" placeholder="Video Title" />}
        {c.video_url ? (
          <div className="max-w-3xl mx-auto rounded-2xl overflow-hidden bg-gray-900 aspect-video">
            <video src={c.video_url} controls className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="max-w-3xl mx-auto h-64 bg-gray-900 rounded-2xl flex flex-col items-center justify-center cursor-pointer group" onClick={(e) => { e.stopPropagation(); onEdit(block.id, '_open_panel', true); }}>
            <Play size={48} className="text-white/30 group-hover:text-white/60 transition-all" />
            <p className="text-white/40 text-sm mt-3">Click to add video URL</p>
          </div>
        )}
      </div>
    </FullBlock>
  );
}

// ─── BOOKING ─────────────────────────────────────────────────────────────
export function BookingBlock({ block, content: c, style: s, E, Toolbar, FullBlock, BgLayer }) {
  return (
    <FullBlock block={block}>{Toolbar()}
      <BgLayer defaultBg="bg-blue-50" center hasPadding>
        <E value={c.title} field="title" className="text-3xl font-bold" tag="h2" placeholder="Book an Appointment" />
        <E value={c.subtitle} field="subtitle" className="opacity-60 mt-2 text-lg" tag="p" placeholder="Choose a time that works" />
        <div className="flex gap-4 justify-center mt-8 flex-wrap">
          {['Consultation', 'Full Session', 'Premium'].map(sv => (
            <div key={sv} className="px-8 py-5 border border-current/10 rounded-2xl bg-white/80 text-center hover:shadow-lg transition-all">
              <p className="font-bold">{sv}</p><p className="opacity-50 text-sm mt-1">60 min</p>
            </div>
          ))}
        </div>
        <button className="mt-8 px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all">Book Now</button>
        <SectionButtons block={block} />
      </BgLayer>
    </FullBlock>
  );
}

// ─── DONATIONS ───────────────────────────────────────────────────────────
// ─── DONATIONS ───────────────────────────────────────────────────────────
// Iter 171 — 6 layout variants: centered (default), side-image,
// image-bg, image-hero, marquee, stats. Each renders the same form
// primitives (title, subtitle, amount chips, CTA, optional PayPal)
// but with different framing so vendors can pick a tone that matches
// their cause. `block.layout` switches between variants.
function DonationForm({ block, content: c, E, accent = 'bg-rose-500' }) {
  const amounts = (c.amounts && c.amounts.length ? c.amounts : [25, 50, 100, 250]);
  // amounts can be either numbers (legacy) or { value, description } (fund-backed)
  const renderAmt = (a, i) => {
    const val = typeof a === 'number' ? a : a?.value;
    if (val == null) return null;
    return (
      <span
        key={i}
        className="px-4 py-2.5 border border-current/20 rounded-xl bg-white/80 text-gray-900 font-semibold text-sm hover:border-rose-400 transition-all cursor-default"
      >${val}</span>
    );
  };
  return (
    <div className="space-y-4">
      <div>
        <E value={c.title} field="title" className="text-3xl font-bold leading-tight" tag="h2" placeholder="Make a Donation" />
        <E value={c.subtitle} field="subtitle" className="opacity-60 mt-1 text-sm" tag="p" placeholder="Every gift helps us continue this work" />
      </div>
      <div className="flex flex-wrap gap-2">
        {amounts.map(renderAmt)}
        {c.custom_amounts_enabled !== false && (
          <span className="px-4 py-2.5 border border-current/15 rounded-xl bg-white/40 text-gray-500 text-sm italic">Custom</span>
        )}
      </div>
      {c.cover_fees_enabled && (
        <label className="flex items-start gap-2 text-xs text-gray-700 select-none">
          <input type="checkbox" className="mt-0.5" defaultChecked readOnly />
          <span>{c.cover_fees_description || 'Support us by covering the fees we have to pay'}</span>
        </label>
      )}
      <div className="flex gap-2">
        {c.accept_card !== false && (
          <button className={`flex-1 sm:flex-none px-6 py-3 ${accent} text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2`}>
            <CreditCard size={14} /> {c.cta_label || 'Donate with Card'}
          </button>
        )}
        {c.accept_paypal && (
          <button className="px-6 py-3 bg-[#0070BA] text-white rounded-xl font-semibold text-sm flex items-center gap-2">
            <Globe size={14} /> PayPal
          </button>
        )}
      </div>
      {c.disclaimer && (
        <p className="text-[10.5px] text-gray-500 leading-relaxed">{c.disclaimer}</p>
      )}
    </div>
  );
}

function FundLink({ c, onEdit, block }) {
  // Subtle anchor that deep-links to the donation fund settings. When a
  // fund_id is set, jump straight to that fund; otherwise open the index.
  const href = c.fund_id
    ? `/website-builder/settings/donations/${c.fund_id}`
    : '/website-builder/settings/donations';
  return (
    <a
      href={href}
      data-testid={`donations-fund-link-${block.id}`}
      onClick={(e) => { e.stopPropagation(); }}
      className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/65 text-white text-[10px] uppercase tracking-wider font-bold backdrop-blur hover:bg-black/85"
      title="Open this fund's settings"
    >
      Edit amounts <ArrowRightIcon size={10} />
    </a>
  );
}

// Tiny inline arrow to avoid pulling another lucide icon for one use.
const ArrowRightIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

export function DonationsBlock({ block, content: c, style: s, E, Toolbar, FullBlock, BgLayer }) {
  const layout = block.layout || 'centered';
  const img = c.image_url || c.image || 'https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=1200&q=70';
  const stats = (c.stats && c.stats.length ? c.stats : [
    { value: '125+', label: 'People helped' },
    { value: '$48k', label: 'Raised' },
    { value: '12', label: 'Programs' },
  ]);

  // SIDE-IMAGE — form left, full-bleed image right (Squarespace "Make a Donation")
  if (layout === 'side-image') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="relative grid grid-cols-1 md:grid-cols-2 bg-stone-50 text-gray-900 min-h-[520px]" data-donations-layout="side-image">
          <FundLink c={c} block={block} />
          <div className="p-10 sm:p-14 flex items-center"><div className="max-w-md w-full"><DonationForm block={block} content={c} E={E} /></div></div>
          <div className="relative min-h-[280px] md:min-h-full">
            <WBImage src={img} alt="" blockId={block.id} field="image_url" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  // IMAGE-BG — form floating over background image (Squarespace "Make an Impact Today")
  if (layout === 'image-bg') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="relative bg-stone-100 text-gray-900 overflow-hidden min-h-[560px] flex items-center justify-center p-6 sm:p-10" data-donations-layout="image-bg">
          <FundLink c={c} block={block} />
          <WBImage src={img} alt="" blockId={block.id} field="image_url" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/35" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl w-full items-center">
            <div className="text-white">
              <E value={c.title} field="title" className="text-4xl sm:text-5xl font-bold leading-tight font-syne" tag="h2" placeholder="Make an Impact Today" />
              <E value={c.subtitle} field="subtitle" className="mt-3 opacity-85 text-base max-w-md" tag="p" placeholder="Your support fuels our programs and brings hope where it's needed most." />
            </div>
            <div className="bg-white rounded-2xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.45)] p-8"><DonationForm block={block} content={c} E={E} accent="bg-black" /></div>
          </div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  // IMAGE-HERO — wide image on top, form below (Squarespace "Make a Donation" with hero)
  if (layout === 'image-hero') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="relative bg-stone-50 text-gray-900" data-donations-layout="image-hero">
          <FundLink c={c} block={block} />
          <div className="relative h-72 sm:h-80 overflow-hidden">
            <WBImage src={img} alt="" blockId={block.id} field="image_url" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-stone-50" />
          </div>
          <div className="px-6 sm:px-10 -mt-20 relative pb-14"><div className="max-w-xl mx-auto bg-white rounded-2xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25)] p-8"><DonationForm block={block} content={c} E={E} /></div></div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  // MARQUEE — scrolling banner above form
  if (layout === 'marquee') {
    const marqueeText = c.marquee_text || 'Make an Impact Today  ·  Give Now  ·  Make an Impact Today  ·  Give Now';
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="relative bg-stone-50 text-gray-900 py-14 px-6" data-donations-layout="marquee">
          <FundLink c={c} block={block} />
          <div className="overflow-hidden py-3 mb-10 border-y border-current/10">
            <div className="whitespace-nowrap font-syne text-4xl sm:text-6xl font-bold animate-[marquee_25s_linear_infinite] opacity-85">{marqueeText} &nbsp; {marqueeText}</div>
          </div>
          <div className="max-w-md mx-auto"><DonationForm block={block} content={c} E={E} /></div>
          <style>{'@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }'}</style>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  // STATS — impact stats grid + form
  if (layout === 'stats') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="relative bg-stone-50 text-gray-900 px-6 sm:px-12 py-16" data-donations-layout="stats">
          <FundLink c={c} block={block} />
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <E value={c.title} field="title" className="text-4xl sm:text-5xl font-bold leading-tight font-syne" tag="h2" placeholder="Empower Others. Create Change." />
              <E value={c.subtitle} field="subtitle" className="mt-3 opacity-65 text-base max-w-md" tag="p" placeholder="Every dollar moves the needle for someone in our community." />
              <div className="mt-8 grid grid-cols-3 gap-3">
                {stats.slice(0, 3).map((st, i) => (
                  <div key={i} className="p-4 rounded-xl bg-white border border-stone-200 text-center">
                    <div className="text-2xl font-bold font-syne">{st.value}</div>
                    <div className="text-[10.5px] uppercase tracking-wider text-gray-500 mt-1">{st.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-stone-200 p-8"><DonationForm block={block} content={c} E={E} accent="bg-black" /></div>
          </div>
          <SectionButtons block={block} />
        </div>
      </FullBlock>
    );
  }

  // CENTERED (default) — the original look but with editing affordances
  return (
    <FullBlock block={block}>{Toolbar()}
      <BgLayer defaultBg="bg-rose-50" center hasPadding>
        <FundLink c={c} block={block} />
        <div className="max-w-md mx-auto"><DonationForm block={block} content={c} E={E} /></div>
        <SectionButtons block={block} />
      </BgLayer>
    </FullBlock>
  );
}

// ─── FORM ────────────────────────────────────────────────────────────────
export function FormBlock({ block, content: c, E, Toolbar, FullBlock, onEdit }) {
  const layout = block.layout || 'contact';

  if (layout === 'quote') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-16 px-8" style={{ background: '#FAF6EE' }}>
          <div className="max-w-md mx-auto">
            <E value={c.title} field="title" className="text-2xl font-extrabold text-gray-900" tag="h2" placeholder="Request a Quote" />
            <E value={c.description} field="description" className="text-gray-500 mt-2 text-sm" tag="p" placeholder="Tell us about your project" />
            <div className="space-y-3 mt-6">
              <div className="bg-white border border-gray-200 rounded-lg h-11 px-3 flex items-center text-sm text-gray-400">Name</div>
              <div className="bg-white border border-gray-200 rounded-lg h-11 px-3 flex items-center text-sm text-gray-400">Email</div>
              <div className="grid grid-cols-3 gap-2">
                {['Project', 'Budget', 'Timeline'].map((t,i)=>(
                  <div key={i} className="bg-white border border-gray-200 rounded-lg h-11 px-3 flex items-center text-xs text-gray-400">{t}</div>
                ))}
              </div>
            </div>
            <button className="w-full mt-6 py-3 rounded-lg text-xs font-extrabold uppercase tracking-wider text-white" style={{ background: '#8B5CF6' }}>Get Quote</button>
          </div>
        </div>
      </FullBlock>
    );
  }

  if (layout === 'feedback') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-16 px-8 bg-white text-center">
          <div className="max-w-md mx-auto">
            <E value={c.title} field="title" className="text-2xl font-extrabold text-gray-900" tag="h2" placeholder="Tell us what you think" />
            <E value={c.description} field="description" className="text-gray-500 mt-2 text-sm" tag="p" placeholder="Your feedback helps us improve" />
            <div className="flex justify-center gap-4 mt-8">
              {['😞','😐','🙂','😀','🤩'].map((e,i)=>(
                <div key={i} className={`w-14 h-14 rounded-full flex items-center justify-center text-3xl cursor-pointer ${i === 3 ? '' : 'bg-gray-100'}`} style={i === 3 ? { background: '#FCD34D' } : {}}>{e}</div>
              ))}
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg h-24 mt-6 px-3 py-2 text-sm text-gray-400 text-left">Tell us more (optional)…</div>
            <button className="mt-6 px-8 py-3 rounded-lg text-xs font-extrabold uppercase tracking-wider bg-gray-900 text-white">Submit</button>
          </div>
        </div>
      </FullBlock>
    );
  }

  if (layout === 'rsvp') {
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-16 px-8" style={{ background: '#FAF6EE' }}>
          <div className="max-w-md mx-auto">
            <E value={c.title} field="title" className="text-3xl font-extrabold text-gray-900 text-center" tag="h2" placeholder="RSVP" />
            <E value={c.description} field="description" className="text-gray-500 text-center mt-2 text-sm" tag="p" placeholder="Will you be joining us?" />
            <div className="grid grid-cols-3 gap-3 mt-6">
              <button className="py-3 rounded-lg text-xs font-extrabold uppercase tracking-wider text-white" style={{ background: '#10B981' }}>✓ Yes</button>
              <button className="py-3 rounded-lg text-xs font-extrabold uppercase tracking-wider bg-gray-100 text-gray-600">✗ No</button>
              <button className="py-3 rounded-lg text-xs font-extrabold uppercase tracking-wider bg-gray-100 text-gray-600">? Maybe</button>
            </div>
            <div className="space-y-3 mt-5">
              <div className="bg-white border border-gray-200 rounded-lg h-11 px-3 flex items-center text-sm text-gray-400">Name</div>
              <div className="bg-white border border-gray-200 rounded-lg h-11 px-3 flex items-center text-sm text-gray-400">Email</div>
              <div className="bg-white border border-gray-200 rounded-lg h-11 px-3 flex items-center text-sm text-gray-400">+1 Guest? (optional)</div>
            </div>
            <button className="w-full mt-5 py-3 rounded-lg text-xs font-extrabold uppercase tracking-wider bg-gray-900 text-white">Send RSVP</button>
          </div>
        </div>
      </FullBlock>
    );
  }

  if (layout === 'survey') {
    const qs = c.questions || [
      'How likely are you to recommend us?',
      'Which feature do you use most?',
      'How can we improve?',
    ];
    return (
      <FullBlock block={block}>{Toolbar()}
        <div className="py-16 px-8 bg-white">
          <div className="max-w-md mx-auto">
            <p className="text-xs font-extrabold tracking-[0.2em] uppercase" style={{ color: '#8B5CF6' }}>Question 1 of {qs.length}</p>
            <E value={c.title} field="title" className="text-xl font-extrabold text-gray-900 mt-3" tag="h2" placeholder={qs[0]} />
            <div className="space-y-3 mt-6">
              {['Extremely likely','Very likely','Somewhat likely','Not at all'].map((opt,i)=>(
                <label key={i} className="flex items-center gap-3 cursor-pointer">
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${i === 1 ? '' : ''}`} style={{ borderColor: '#8B5CF6', background: i === 1 ? '#8B5CF6' : 'transparent' }}>
                    {i === 1 && <span className="w-2 h-2 rounded-full bg-white" />}
                  </span>
                  <span className="text-sm text-gray-700">{opt}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-between mt-8 pt-4 border-t border-gray-100">
              <button className="text-xs font-bold uppercase tracking-wider text-gray-500">← Back</button>
              <button className="px-5 py-2.5 rounded-lg text-xs font-extrabold uppercase tracking-wider text-white" style={{ background: '#8B5CF6' }}>Next →</button>
            </div>
          </div>
        </div>
      </FullBlock>
    );
  }

  // Default · 'contact' — 2-col name+email then message, dark SEND button
  return (
    <FullBlock block={block}>{Toolbar()}
      <div className="py-16 px-8 bg-gray-50">
        <div className="max-w-md mx-auto">
          <E value={c.title} field="title" className="text-2xl font-extrabold text-gray-900 text-center" tag="h2" placeholder="Contact Us" />
          <E value={c.description} field="description" className="text-gray-500 text-center mt-2" tag="p" placeholder="We'd love to hear from you" />
          <div className="space-y-3 mt-8">
            {(c.fields || []).length > 0 ? c.fields.map((f, i) => (
              <div key={i}><label className="text-sm font-medium text-gray-700">{f.label}</label><div className={`mt-1 bg-white border border-gray-200 rounded-lg ${f.type === 'textarea' ? 'h-24' : 'h-11'}`} /></div>
            )) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white border border-gray-200 rounded-lg h-11 px-3 flex items-center text-sm text-gray-400">Name</div>
                  <div className="bg-white border border-gray-200 rounded-lg h-11 px-3 flex items-center text-sm text-gray-400">Email</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg h-11 px-3 flex items-center text-sm text-gray-400">Subject</div>
                <div className="bg-white border border-gray-200 rounded-lg h-24 px-3 py-2 text-sm text-gray-400">Message…</div>
              </>
            )}
          </div>
          <button className="w-full mt-6 py-3 bg-gray-900 text-white rounded-lg text-xs font-extrabold uppercase tracking-wider">Send</button>
        </div>
      </div>
    </FullBlock>
  );
}

// ─── BANNER ──────────────────────────────────────────────────────────────
export function BannerBlock({ block, content: c, style: s, E, Toolbar, FullBlock, onEdit, bgColor, bgImage, blockStyle }) {
  return (
    <FullBlock block={block}>{Toolbar()}
      <div
        className="relative overflow-hidden flex items-center"
        style={{
          ...(bgColor ? { backgroundColor: bgColor } : {}),
          minHeight: c.height === 'short' ? 150 : c.height === 'tall' ? 400 : c.height === 'fullscreen' ? '80vh' : 260,
        }}
      >
        {(bgImage || c.image_url) ? (
          <WBImage
            src={bgImage || c.image_url}
            alt=""
            blockId={block.id}
            field="image_url"
            frame={(c._image_frames || {}).image_url}
            className="absolute inset-0 w-full h-full"
            imgStyle={{
              objectPosition: c.focus_point
                ? `${c.focus_point.x ?? 50}% ${c.focus_point.y ?? 50}%`
                : '50% 50%',
            }}
          />
        ) : !bgColor && <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600" />}
        <div className="absolute inset-0 bg-black/30 pointer-events-none" />
        <div className="absolute top-2 right-2 z-20 opacity-0 group-hover/block:opacity-100 transition-all">
          <label className="px-2 py-1 bg-white/80 rounded text-xs font-medium text-gray-800 cursor-pointer hover:bg-white">
            <ImageIcon size={10} className="inline mr-1" />Add Photo
            <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
              const file = e.target.files[0]; if (!file) return;
              const fd = new FormData(); fd.append('file', file);
              try { const r = await axios.post(`${API}/media/upload`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' } }); onEdit(block.id, 'image_url', r.data.url); } catch {}
            }} />
          </label>
        </div>
        <div className="relative z-10 text-center text-white w-full px-8" style={blockStyle}>
          <E value={c.text} field="text" className="text-lg font-semibold" tag="p" placeholder="Announcement banner..." />
          <SectionButtons block={block} />
        </div>
      </div>
    </FullBlock>
  );
}

// ─── EMBED ───────────────────────────────────────────────────────────────
export function EmbedBlock({ block, content: c, E, Toolbar, FullBlock, onEdit }) {
  return (
    <FullBlock block={block}>{Toolbar()}
      <div className="py-12 px-8 bg-gray-50">
        {c.title && <E value={c.title} field="title" className="text-xl font-bold text-gray-900 text-center mb-4" tag="h2" placeholder="Title" />}
        {c.url ? (
          <div className="max-w-3xl mx-auto p-4 bg-white rounded-xl border border-gray-200 flex items-center gap-3"><Globe size={18} className="text-gray-400 shrink-0" /><p className="text-gray-600 text-sm truncate">{c.url}</p></div>
        ) : (
          <div className="max-w-3xl mx-auto h-40 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center cursor-pointer hover:border-blue-400 transition-all" onClick={(e) => { e.stopPropagation(); onEdit(block.id, '_open_panel', true); }}><p className="text-gray-400">Click to add embed URL</p></div>
        )}
      </div>
    </FullBlock>
  );
}

// ─── SPACER ──────────────────────────────────────────────────────────────
export function SpacerBlock({ block, content: c, Toolbar, FullBlock }) {
  return (
    <FullBlock block={block}>{Toolbar()}
      <div style={{ height: parseInt(c.height) || 80 }} className="bg-white relative">
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/block:opacity-100 transition-all">
          <span className="text-xs text-gray-400 bg-white px-2 py-1 rounded border border-gray-200">Spacer — {c.height || '80px'}</span>
        </div>
      </div>
    </FullBlock>
  );
}

// ─── MARQUEE ─────────────────────────────────────────────────────────────
export function MarqueeBlock({ block, content: c, Toolbar, FullBlock }) {
  return (
    <FullBlock block={block}>{Toolbar()}
      <MarqueeRenderer content={c} />
    </FullBlock>
  );
}

// ─── FLUID CANVAS ────────────────────────────────────────────────────────
export function FluidCanvasBlock({ block, content: c, Toolbar, FullBlock, onEdit }) {
  return (
    <FullBlock block={block}>{Toolbar()}
      <FluidCanvas
        block={block}
        content={c}
        onUpdateChild={(childId, patch) => {
          const next = (c.children || []).map(ch => ch.id === childId ? { ...ch, ...patch } : ch);
          onEdit(block.id, 'children', next);
        }}
        onAddChild={(child) => onEdit(block.id, 'children', [...(c.children || []), child])}
        onRemoveChild={(childId) => onEdit(block.id, 'children', (c.children || []).filter(ch => ch.id !== childId))}
        editable={true}
      />
    </FullBlock>
  );
}
